import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Divider,
  Paper,
  alpha,
  useTheme,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Person as PersonIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import EnviarParaAtendenteModal from './EnviarParaAtendenteModal';

// Componente compacto para visualização em grid
const SenhaAtivaGrid = ({ senha, posicao, onExcluir, onEnviarParaAtendente }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogoConfirmacaoAberto, setDialogoConfirmacaoAberto] = useState(false);
  const [modalAtendenteAberto, setModalAtendenteAberto] = useState(false);
  
  const open = Boolean(anchorEl);
  
  // Calcular tempo de espera
  const calcularTempoEspera = () => {
    const dataEmissao = new Date(senha.emitida_em);
    const agora = new Date();
    const diferencaMs = agora - dataEmissao;
    const minutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (minutos < 60) {
      return `${minutos}min`;
    } else {
      const horas = Math.floor(minutos / 60);
      const minutosRestantes = minutos % 60;
      return `${horas}h ${minutosRestantes}min`;
    }
  };
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleExcluirClick = () => {
    handleClose();
    setDialogoConfirmacaoAberto(true);
  };
  
  const handleEnviarParaAtendenteClick = () => {
    handleClose();
    setModalAtendenteAberto(true);
  };
  
  const handleConfirmarExclusao = () => {
    if (onExcluir) {
      onExcluir(senha.id);
    }
    setDialogoConfirmacaoAberto(false);
  };
  
  const handleCancelarExclusao = () => {
    setDialogoConfirmacaoAberto(false);
  };
  
  const handleConfirmarEnvioAtendente = (senhaId, atendente) => {
    if (onEnviarParaAtendente) {
      onEnviarParaAtendente(senhaId, atendente);
    }
    setModalAtendenteAberto(false);
  };
  
  const handleFecharModalAtendente = () => {
    setModalAtendenteAberto(false);
  };
  
  return (
    <>
      <Card 
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: senha.preferencial 
            ? `4px solid ${theme.palette.warning.main}` 
            : `4px solid ${theme.palette.primary.main}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)'
          },
          position: 'relative' 
        }}
      >
        <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}>
          {/* Header com número da senha e posição */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.4rem' }}>
              {senha.senha || senha.numero}
            </Typography>
            <Chip 
              label={posicao} 
              size="small" 
              color="secondary"
              sx={{ 
                fontWeight: 'bold', 
                minWidth: '32px', 
                mr: (onExcluir || onEnviarParaAtendente) ? 3 : 0 
              }}
            />
          </Box>
          
          {/* Chips, tempo de espera e menu de opções */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
            {/* Tags lado a lado */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {senha.preferencial && (
                <Chip 
                  icon={<StarIcon fontSize="small" />} 
                  label="Preferencial" 
                  size="small" 
                  color="warning"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
              <Chip 
                label={senha.servico_nome || senha.tipo} 
                size="small" 
                color="primary"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
              
              {/* Tempo de espera na mesma linha */}
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', ml: 1, position: 'absolute', right: 15 }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {calcularTempoEspera()}
                </Typography>
              </Box>
            </Box>
            
            {/* Menu de opções */}
            {(onExcluir || onEnviarParaAtendente) && (
              <IconButton
                size="small"
                aria-label="mais opções"
                onClick={handleClick}
                sx={{ p: 0.5, position: 'absolute', right: 10, top: 18 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Menu de opções */}
      <Menu
        id="senha-menu-grid"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'menu-button',
        }}
        PaperProps={{
          sx: {
            boxShadow: theme.shadows[3],
            borderRadius: 2,
          }
        }}
      >
        {onEnviarParaAtendente && (
          <MenuItem onClick={handleEnviarParaAtendenteClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Enviar para Atendente</ListItemText>
          </MenuItem>
        )}
        {onExcluir && (
          <MenuItem onClick={handleExcluirClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excluir Senha</ListItemText>
          </MenuItem>
        )}
      </Menu>
      
      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={dialogoConfirmacaoAberto}
        onClose={handleCancelarExclusao}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5]
          }
        }}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a senha <strong>{senha.senha || senha.numero}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCancelarExclusao} 
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmarExclusao} 
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, ml: 1 }}
            autoFocus
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal para enviar para atendente */}
      <EnviarParaAtendenteModal
        open={modalAtendenteAberto}
        onClose={handleFecharModalAtendente}
        onConfirm={handleConfirmarEnvioAtendente}
        senha={senha}
      />
    </>
  );
};

// Componente para exibir uma senha individual (visualização em lista)
const SenhaAtiva = ({ senha, posicao, onExcluir, onEnviarParaAtendente }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogoConfirmacaoAberto, setDialogoConfirmacaoAberto] = useState(false);
  const [modalAtendenteAberto, setModalAtendenteAberto] = useState(false);
  
  console.log('SenhaAtiva - Dados da senha:', {
    id: senha.id,
    numero: senha.senha || senha.numero,
    tipo: senha.servico_nome || senha.tipo,
    preferencial: senha.preferencial,
    emitida_em: senha.emitida_em
  });
  
  const open = Boolean(anchorEl);
  
  // Calcular tempo de espera
  const calcularTempoEspera = () => {
    const dataEmissao = new Date(senha.emitida_em);
    const agora = new Date();
    const diferencaMs = agora - dataEmissao;
    const minutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (minutos < 60) {
      return `${minutos} min`;
    } else {
      const horas = Math.floor(minutos / 60);
      const minutosRestantes = minutos % 60;
      return `${horas}h ${minutosRestantes}min`;
    }
  };
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleExcluirClick = () => {
    handleClose();
    setDialogoConfirmacaoAberto(true);
  };
  
  const handleEnviarParaAtendenteClick = () => {
    handleClose();
    setModalAtendenteAberto(true);
  };
  
  const handleConfirmarExclusao = () => {
    if (onExcluir) {
      onExcluir(senha.id);
    }
    setDialogoConfirmacaoAberto(false);
  };
  
  const handleCancelarExclusao = () => {
    setDialogoConfirmacaoAberto(false);
  };
  
  const handleConfirmarEnvioAtendente = (senhaId, atendente) => {
    console.group('🔄 SENHAS ATIVAS LIST: Confirmando envio para atendente');
    console.log('📋 senhaId recebido:', senhaId);
    console.log('👤 atendente recebido:', atendente);
    console.log('🆔 atendente.id:', atendente?.id);
    console.log('📛 atendente.nome:', atendente?.nome);
    console.log('🏷️ typeof atendente:', typeof atendente);
    console.groupEnd();

    if (onEnviarParaAtendente) {
      onEnviarParaAtendente(senhaId, atendente);
    }
    setModalAtendenteAberto(false);
  };
  
  const handleFecharModalAtendente = () => {
    setModalAtendenteAberto(false);
  };
  
  return (
    <>
      <Card 
        sx={{
          mb: 2,
          borderLeft: senha.preferencial 
            ? `5px solid ${theme.palette.warning.main}` 
            : `5px solid ${theme.palette.primary.main}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', p: 2, '&:last-child': { pb: 2 } }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mr: 1 }}>
                {senha.senha || senha.numero}
              </Typography>
              {senha.preferencial && (
                <Chip 
                  icon={<StarIcon fontSize="small" />} 
                  label="Preferencial" 
                  size="small" 
                  color="warning"
                  sx={{ mr: 1 }}
                />
              )}
              <Chip 
                label={senha.servico_nome || senha.tipo} 
                size="small" 
                color="primary"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                Espera: {calcularTempoEspera()}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip 
              label={`Posição: ${posicao}`} 
              size="small" 
              color="secondary"
              sx={{ fontWeight: 'bold', mr: 1 }}
            />
            
            {/* Só exibe o menu se houver pelo menos uma ação disponível */}
            {(onExcluir || onEnviarParaAtendente) && (
              <IconButton
                aria-label="mais opções"
                aria-controls="senha-menu"
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Menu de opções */}
      <Menu
        id="senha-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'menu-button',
        }}
        PaperProps={{
          sx: {
            boxShadow: theme.shadows[3],
            borderRadius: 2
          }
        }}
      >
        {onEnviarParaAtendente && (
          <MenuItem onClick={handleEnviarParaAtendenteClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Enviar para Atendente</ListItemText>
          </MenuItem>
        )}
        {onExcluir && (
          <MenuItem onClick={handleExcluirClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excluir Senha</ListItemText>
          </MenuItem>
        )}
      </Menu>
      
      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={dialogoConfirmacaoAberto}
        onClose={handleCancelarExclusao}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5]
          }
        }}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a senha <strong>{senha.senha || senha.numero}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCancelarExclusao} 
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmarExclusao} 
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, ml: 1 }}
            autoFocus
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal para enviar para atendente */}
      <EnviarParaAtendenteModal
        open={modalAtendenteAberto}
        onClose={handleFecharModalAtendente}
        onConfirm={handleConfirmarEnvioAtendente}
        senha={senha}
      />
    </>
  );
};

// Componente principal da lista de senhas ativas
const SenhasAtivasList = ({ senhas, onExcluir, onEnviarParaAtendente }) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'grid'
  
  console.log('SenhasAtivasList - Senhas recebidas:', senhas);
  
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };
  
  // Se não houver senhas, exibe mensagem
  if (!senhas || senhas.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          p: 4
        }}
      >
        <ForwardToInboxIcon
          sx={{
            fontSize: 80,
            color: 'text.disabled',
            mb: 2
          }}
        />
        <Typography variant="h6" color="text.secondary" align="center">
          Não há senhas aguardando atendimento no momento
        </Typography>
        <Typography variant="body2" color="text.disabled" align="center" sx={{ mt: 1 }}>
          As senhas aparecerão aqui quando forem emitidas
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controles de visualização */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, flexShrink: 0 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="modo de visualização"
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                }
              }
            }
          }}
        >
          <ToggleButton value="list" aria-label="visualização em lista">
            <Tooltip title="Visualização em Lista">
              <ViewListIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="grid" aria-label="visualização em grid">
            <Tooltip title="Visualização em Grade">
              <ViewModuleIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Conteúdo das senhas */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          pr: 1,
          pl: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }
          }
        }}
      >
        {viewMode === 'list' ? (
          // Visualização em lista
          <Box>
            {senhas.map((senha, index) => (
              <SenhaAtiva
                key={senha.id}
                senha={senha}
                posicao={index + 1}
                onExcluir={onExcluir}
                onEnviarParaAtendente={onEnviarParaAtendente}
              />
            ))}
          </Box>
        ) : (
          // Visualização em grid
          <Grid container spacing={2}>
            {senhas.map((senha, index) => (
              <Grid item xs={12} sm={6} md={4} key={senha.id}>
                <SenhaAtivaGrid
                  senha={senha}
                  posicao={index + 1}
                  onExcluir={onExcluir}
                  onEnviarParaAtendente={onEnviarParaAtendente}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default SenhasAtivasList; 